import { Box, Divider, Grid } from '@material-ui/core'
import React, { ReactElement } from 'react'
import {
  DeleteWithConfirmButton,
  EditButton,
  FunctionField,
  ReferenceField,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  TextField,
  TextInput,
  TitleProps,
  useShowContext,
  useTranslate,
} from 'react-admin'
import RelationTab from '../../../common/RelationTab'
import relationTabFilter from '../../../common/RelationTabFilter'
import { ResourceName } from '../../../../core/ResourceName'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { EngineMediaFileDto } from '../../../../core/dto/engine-media/engine-media-file.dto'
import { EngineMediaActionDto } from '../../../../core/dto/engine-media/engine-media-action.dto'
import { FilePathDto } from '../../../../core/dto/files/FilesPath.dto'

interface EngineMediaFilesTitleProps extends TitleProps {
  record?: EngineMediaFileDto
}

const ShowTitle = (data: EngineMediaFilesTitleProps) => {
  const { record: file } = data
  const translate = useTranslate()

  return (
    <span>
      {translate('resources.engine-media-files.titles.show')}: {file?.id}
    </span>
  )
}

const GeneralActions = () => {
  const { record: file } = useShowContext<EngineMediaFileDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1} display="flex" justifyContent="flex-end">
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item style={{ margin: '2px' }}>
            <DeleteWithConfirmButton
              basePath={`/${ResourceName.ENGINE_MEDIA_FILES}`}
              record={file}
              variant="contained"
              disabled={!hasAuthority(Authority.DELETE_ENGINE_MEDIA_FILES)}
            />
          </Grid>
          <Grid item>
            <EditButton
              variant="contained"
              basePath="/engine-media-files"
              record={file}
              style={{ margin: '2px' }}
              disabled={!hasAuthority(Authority.EDIT_ENGINE_MEDIA_FILES)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const EngineMediaFileTabs = ({ ...props }) => {
  const translate = useTranslate()
  const { record: file } = props
  const hasAuthority = useHasAuthority()

  const relationFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="description" alwaysOn />],
      resource: ResourceName.ENGINE_MEDIA_FILES,
    })

  return (
    <TabbedShowLayout
      {...props}
      tabs={
        <TabbedShowLayoutTabs
          variant="scrollable"
          scrollButtons="auto"
          {...props}
        />
      }
    >
      <Tab label={translate('resources.engine-media-files.tabs.general')}>
        <TextField source="id" label="ID" />
        <TextField source="description" />
        {hasAuthority(Authority.VIEW_ENGINE_MEDIA_ACTIONS) && (
          <ReferenceField
            source="engineMediaActionId"
            reference={ResourceName.ENGINE_MEDIA_ACTIONS}
            link={hasAuthority(Authority.EDIT_ENGINE_MEDIA_ACTIONS) && 'edit'}
          >
            <FunctionField<EngineMediaActionDto>
              render={(record?: EngineMediaActionDto) =>
                `${record?.id}. ${record?.name} (${record?.mediaAction})`
              }
            />
          </ReferenceField>
        )}
        <FunctionField<EngineMediaFileDto>
          source="mediaContentType"
          render={(record?: EngineMediaFileDto) =>
            translate(
              `resources.enums.MediaContentTypes.${record?.mediaContentType}`,
            )
          }
        />
        {hasAuthority(Authority.VIEW_FILES) && (
          <ReferenceField
            source="filePathId"
            reference={ResourceName.FILES}
            link="show"
          >
            <FunctionField<FilePathDto>
              render={(record?: FilePathDto) =>
                `${record?.id}. ${record?.name}`
              }
            />
          </ReferenceField>
        )}
        <GeneralActions />
      </Tab>
      {hasAuthority(Authority.VIEW_ENGINE_MEDIA_PACKS) && (
        <Tab
          path="packs"
          label={translate('resources.engine-media-files.tabs.packs')}
        >
          <>
            <RelationTab<EngineMediaFileDto>
              resource={ResourceName.ENGINE_MEDIA_PACKS}
              source="engineMediaPacksIds"
              mode={
                hasAuthority(Authority.EDIT_ENGINE_MEDIA_FILES)
                  ? 'edit'
                  : 'show'
              }
              attachMethod="attachPacks"
              detachMethod="detachPacks"
              attachRequestPayload={(r, ids) => ({
                engineMediaFileId: r.id,
                engineMediaPacksIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                engineMediaFileId: r.id,
                engineMediaPacksIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterFileId === true
              }
              filters={relationFilters() as ReactElement[]}
              filterDefaultValues={{
                fileId: file?.id,
                filterFileId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField source="id" label="ID" />
              <TextField source="description" key="description" />
            </RelationTab>
          </>
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const EngineMediaFileShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <EngineMediaFileTabs />
  </Show>
)
